import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  classifyRequisitionItems,
  completeReview,
  fetchActivities,
  fetchChartOfAccounts,
  fetchCostCenters,
  fetchRequisitionReview,
  fetchReviewStates,
  saveBulkReview,
} from './forms.apis';


import { ILookupItem } from './forms.models';
import {
  IClassificationSuggestion,
  IReviewItemRecord,
  IReviewWorkspaceItem,
} from './review.models';

type Props = {
  requisitionix: string;
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  saving: boolean;
  completing: boolean;
  classifying: boolean;
  error: string;
  success: string;

  requisition: any | null;
  items: IReviewWorkspaceItem[];
  reviewDrafts: Record<string, IReviewItemRecord>;
  suggestions: Record<string, IClassificationSuggestion>;

  accounts: ILookupItem[];
  costcenters: ILookupItem[];
  activities: ILookupItem[];
  reviewStates: ILookupItem[];
};

export default class ReviewWorkspacePage extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,
      completing: false,
      classifying: false,
      error: '',
      success: '',

      requisition: null,
      items: [],
      reviewDrafts: {},
      suggestions: {},

      accounts: [],
      costcenters: [],
      activities: [],
      reviewStates: [],
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [reviewData, accounts, costcenters, activities, reviewStates] = await Promise.all([
        fetchRequisitionReview(this.props.requisitionix),
        fetchChartOfAccounts(),
        fetchCostCenters(),
        fetchActivities(),
        fetchReviewStates(),
      ]);

      const reviewDrafts: Record<string, IReviewItemRecord> = {};

      (reviewData.items || []).forEach((item:IReviewWorkspaceItem) => {
        reviewDrafts[item.recix] = {
          recix: item.review?.recix,
          itemix: item.recix,
          accountix: item.review?.account?.recix || '',
          costcenterix: item.review?.costcenter?.recix || '',
          activityix: item.review?.activity?.recix || '',
          approvedamount:
            item.review?.approvedamount === null ||
            item.review?.approvedamount === undefined
              ? ''
              : item.review?.approvedamount,
          remarks: item.review?.remarks || '',
          reviewstateix: item.review?.reviewstate?.recix || '',
          aisuggested: item.review?.aisuggested || 0,
        };
      });

      this.setState({
        requisition: reviewData.requisition,
        items: reviewData.items || [],
        reviewDrafts,
        accounts,
        costcenters,
        activities,
        reviewStates,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load review workspace',
      });
    }
  };

  private handleReviewSelectChange =
    (itemix: string, field: keyof IReviewItemRecord) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        reviewDrafts: {
          ...prevState.reviewDrafts,
          [itemix]: {
            ...prevState.reviewDrafts[itemix],
            [field]: value,
          },
        },
      }));
    };

  private handleReviewTextChange =
    (itemix: string, field: keyof IReviewItemRecord) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        reviewDrafts: {
          ...prevState.reviewDrafts,
          [itemix]: {
            ...prevState.reviewDrafts[itemix],
            [field]: value,
          },
        },
      }));
    };

  private handleReviewNumberChange =
    (itemix: string, field: keyof IReviewItemRecord) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const rawValue = event.target.value;
      const value = rawValue === '' ? '' : Number(rawValue);

      this.setState((prevState) => ({
        reviewDrafts: {
          ...prevState.reviewDrafts,
          [itemix]: {
            ...prevState.reviewDrafts[itemix],
            [field]: value,
          },
        },
      }));
    };

  private findLookupByShortcode = (
    items: ILookupItem[],
    shortcode: string | null
  ): ILookupItem | undefined => {
    if (!shortcode) return undefined;

    return items.find(
      (x) => (x.shortcode || '').toUpperCase() === shortcode.toUpperCase()
    );
  };

  private handleSuggestClassification = async (): Promise<void> => {
    const { requisition, items } = this.state;

    if (!requisition || items.length === 0) {
      return;
    }

    this.setState({
      classifying: true,
      error: '',
      success: '',
    });

    try {
      const result = await classifyRequisitionItems({
        items: items.map((item) => ({
          itemix: item.recix,
          recname: item.recname || null,
          description: item.description || null,
          justification: item.justification || null,
          expectedoutcome: item.expectedoutcome || null,
          technicalspec: item.technicalspec || null,
          kpinote: item.kpinote || null,
          quantity:
            item.quantity === null || item.quantity === undefined
              ? null
              : Number(item.quantity),
          unitestimate:
            item.unitestimate === null || item.unitestimate === undefined
              ? null
              : Number(item.unitestimate),
          totalestimate:
            item.totalestimate === null || item.totalestimate === undefined
              ? null
              : Number(item.totalestimate),
          typecode: item.type?.shortcode || null,
          prioritycode: item.priority?.shortcode || null,
          header: {
            periodcode: requisition.period?.shortcode || null,
            cyclecode: requisition.cycle?.shortcode || null,
            costcentercode: requisition.costcenter?.shortcode || null,
            requisitiontypecode: requisition.type?.shortcode || null,
            purpose: requisition.purpose || null,
          },
        })),
      });

      const suggestions: Record<string, IClassificationSuggestion> = {};
      (result.suggestions || []).forEach((item) => {
        if (item.itemix) {
          suggestions[item.itemix] = item;
        }
      });

      this.setState({
        classifying: false,
        suggestions,
        success: 'Classification suggestions generated.',
      });
    } catch (error: any) {
      this.setState({
        classifying: false,
        error: error?.message || 'Failed to generate classification suggestions',
      });
    }
  };

  private handleApplySuggestion = (itemix: string): void => {
    const { suggestions, accounts, costcenters, activities, reviewDrafts } = this.state;
    const suggestion = suggestions[itemix];

    if (!suggestion) {
      return;
    }

    const account = this.findLookupByShortcode(accounts, suggestion.accountcode);
    const costcenter = this.findLookupByShortcode(costcenters, suggestion.costcentercode);
    const activity = this.findLookupByShortcode(activities, suggestion.activitycode);

    this.setState({
      reviewDrafts: {
        ...reviewDrafts,
        [itemix]: {
          ...reviewDrafts[itemix],
          itemix,
          accountix: account?.recix || reviewDrafts[itemix]?.accountix || '',
          costcenterix:
            costcenter?.recix || reviewDrafts[itemix]?.costcenterix || '',
          activityix:
            activity?.recix || reviewDrafts[itemix]?.activityix || '',
          approvedamount:
            suggestion.approvedamount === null || suggestion.approvedamount === undefined
              ? reviewDrafts[itemix]?.approvedamount || ''
              : suggestion.approvedamount,
          remarks:
            suggestion.rationale || reviewDrafts[itemix]?.remarks || '',
          aisuggested: 1,
        },
      },
      success: 'Suggestion applied to review form.',
    });
  };

  private validateReviews = (): string[] => {
    const { items, reviewDrafts } = this.state;
    const errors: string[] = [];

    items.forEach((item) => {
      const draft = reviewDrafts[item.recix];
      if (!draft) {
        errors.push(`Missing review draft for item ${item.lineno}.`);
        return;
      }

      if (!draft.activityix) {
        errors.push(`Item ${item.lineno}: activity is required.`);
      }

      if (!draft.accountix) {
        errors.push(`Item ${item.lineno}: account is required.`);
      }

      if (!draft.costcenterix) {
        errors.push(`Item ${item.lineno}: cost center is required.`);
      }

      if (draft.approvedamount === '' || Number(draft.approvedamount) < 0) {
        errors.push(`Item ${item.lineno}: approved amount is required.`);
      }

      if (!draft.reviewstateix) {
        errors.push(`Item ${item.lineno}: review state is required.`);
      }
    });

    return errors;
  };

  private handleSaveReviews = async (): Promise<void> => {
    const errors = this.validateReviews();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      const items = Object.values(this.state.reviewDrafts);

      await saveBulkReview(this.props.requisitionix, { items });

      this.setState({
        saving: false,
        success: 'Review records saved successfully.',
      });
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save review records',
      });
    }
  };

  private handleCompleteReview = async (): Promise<void> => {
    const errors = this.validateReviews();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      completing: true,
      error: '',
      success: '',
    });

    try {
      const items = Object.values(this.state.reviewDrafts);
      await saveBulkReview(this.props.requisitionix, { items });
      await completeReview(this.props.requisitionix, { remarks: 'Review completed' });

      this.setState({
        completing: false,
        success: 'Requisition review completed successfully.',
      });
    } catch (error: any) {
      this.setState({
        completing: false,
        error: error?.message || 'Failed to complete review',
      });
    }
  };

  private handleBack = (): void => {
    if (this.props.navigate) {
      this.props.navigate('/forms/reviews/queue');
      return;
    }

    window.location.href = '/forms/reviews/queue';
  };

  render(): React.ReactNode {
    const {
      loading,
      saving,
      completing,
      classifying,
      error,
      success,
      requisition,
      items,
      reviewDrafts,
      suggestions,
      accounts,
      costcenters,
      activities,
      reviewStates,
    } = this.state;

    const requestedTotal = items.reduce(
      (sum, item) => sum + Number(item.totalestimate || 0),
      0
    );

    const approvedTotal = items.reduce((sum, item) => {
      const draft = reviewDrafts[item.recix];
      const val = draft?.approvedamount === '' ? 0 : Number(draft?.approvedamount || 0);
      return sum + val;
    }, 0);

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Review Workspace
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review, classify, and approve requisition items for budgeting.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>

            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={this.handleSuggestClassification}
              disabled={classifying || loading}
            >
              {classifying ? 'Suggesting...' : 'Suggest Classification'}
            </Button>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={this.handleSaveReviews}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save Review'}
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={<DoneAllIcon />}
              onClick={this.handleCompleteReview}
              disabled={completing || loading}
            >
              {completing ? 'Completing...' : 'Complete Review'}
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        {loading || !requisition ? (
          <Card>
            <CardContent>
              <Typography>Loading review workspace...</Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Requisition Summary
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {requisition.recname}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {requisition.description}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        <strong>Reference:</strong> {requisition.reference || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Budget Period:</strong> {requisition.period?.recname || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Cost Center:</strong> {requisition.costcenter?.recname || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Requested Date:</strong>{' '}
                        {requisition.requesteddate
                          ? moment(requisition.requesteddate).format('YYYY-MM-DD')
                          : '-'}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Items
                    </Typography>
                    <Typography variant="h6">{items.length}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Requested Total
                    </Typography>
                    <Typography variant="h6">
                      {requestedTotal.toLocaleString()}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Approved Total
                    </Typography>
                    <Typography variant="h6">
                      {approvedTotal.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Stack spacing={2}>
              {items.map((item) => {
                const review = reviewDrafts[item.recix];
                const suggestion = suggestions[item.recix];

                return (
                  <Card key={item.recix}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {item.lineno}. {item.recname}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {item.description}
                          </Typography>

                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              <strong>Requested Total:</strong>{' '}
                              {Number(item.totalestimate || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Resource Type:</strong> {item.type?.recname || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Justification:</strong> {item.justification || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Expected Outcome:</strong>{' '}
                              {item.expectedoutcome || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Technical Spec:</strong> {item.technicalspec || '-'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>KPI Note:</strong> {item.kpinote || '-'}
                            </Typography>
                          </Stack>

                          {suggestion ? (
                            <Card variant="outlined" sx={{ mt: 2, bgcolor: 'background.default' }}>
                              <CardContent>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                  <Typography variant="subtitle2">
                                    AI Suggestion
                                  </Typography>
                                  <Chip
                                    size="small"
                                    color="primary"
                                    label={
                                      suggestion.confidence === null ||
                                      suggestion.confidence === undefined
                                        ? 'No score'
                                        : `Conf: ${suggestion.confidence}`
                                    }
                                  />
                                </Stack>

                                <Typography variant="body2">
                                  <strong>Account Code:</strong> {suggestion.accountcode || '-'}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Cost Center Code:</strong> {suggestion.costcentercode || '-'}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Activity Code:</strong> {suggestion.activitycode || '-'}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Approved Amount:</strong>{' '}
                                  {suggestion.approvedamount === null ||
                                  suggestion.approvedamount === undefined
                                    ? '-'
                                    : suggestion.approvedamount}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Rationale:</strong> {suggestion.rationale || '-'}
                                </Typography>

                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ mt: 2 }}
                                  onClick={() => this.handleApplySuggestion(item.recix)}
                                >
                                  Apply Suggestion
                                </Button>
                              </CardContent>
                            </Card>
                          ) : null}
                        </Grid>

                        <Grid size={{ xs: 12, md: 8 }}>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormControl fullWidth>
                                <InputLabel id={`account-${item.recix}`}>Account</InputLabel>
                                <Select
                                  labelId={`account-${item.recix}`}
                                  value={review?.accountix || ''}
                                  label="Account"
                                  onChange={this.handleReviewSelectChange(item.recix, 'accountix')}
                                >
                                  {accounts.map((acc) => (
                                    <MenuItem key={acc.recix} value={acc.recix}>
                                      {acc.recname}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormControl fullWidth>
                                <InputLabel id={`cc-${item.recix}`}>Cost Center</InputLabel>
                                <Select
                                  labelId={`cc-${item.recix}`}
                                  value={review?.costcenterix || ''}
                                  label="Cost Center"
                                  onChange={this.handleReviewSelectChange(item.recix, 'costcenterix')}
                                >
                                  {costcenters.map((cc) => (
                                    <MenuItem key={cc.recix} value={cc.recix}>
                                      {cc.recname}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormControl fullWidth>
                                <InputLabel id={`activity-${item.recix}`}>Activity</InputLabel>
                                <Select
                                  labelId={`activity-${item.recix}`}
                                  value={review?.activityix || ''}
                                  label="Activity"
                                  onChange={this.handleReviewSelectChange(item.recix, 'activityix')}
                                >
                                  {activities.map((activity) => (
                                    <MenuItem key={activity.recix} value={activity.recix}>
                                      {activity.recname}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormControl fullWidth>
                                <InputLabel id={`state-${item.recix}`}>Review State</InputLabel>
                                <Select
                                  labelId={`state-${item.recix}`}
                                  value={review?.reviewstateix || ''}
                                  label="Review State"
                                  onChange={this.handleReviewSelectChange(item.recix, 'reviewstateix')}
                                >
                                  {reviewStates.map((state) => (
                                    <MenuItem key={state.recix} value={state.recix}>
                                      {state.recname}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth
                                label="Approved Amount"
                                type="number"
                                value={review?.approvedamount ?? ''}
                                onChange={this.handleReviewNumberChange(item.recix, 'approvedamount')}
                              />
                            </Grid>

                            <Grid size={{ xs: 12, md: 8 }}>
                              <TextField
                                fullWidth
                                label="Remarks"
                                value={review?.remarks || ''}
                                onChange={this.handleReviewTextChange(item.recix, 'remarks')}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </>
        )}
      </Box>
    );
  }
}